import _, {Dictionary, groupBy, isEqualWith} from 'lodash';

import {isTeammateNamespace} from '../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {ResourceMap} from '../../libs/shared/core/src/store/commonAppTypes';
import {CustomViewModel, CustomViewTypesEnum} from '../models/api/customViewModel';
import {CollapsedItemKeyPrefixesEnum} from '../models/api/localPreferencesModel';
import {TagModel} from '../models/api/tagModel';
import {
  computeWorkspaceSortOrderMap,
  WorkspaceSortOrderMap,
} from '../store/data/workspace/sort/workspaceSortOrderMapHelpers';
import {CollapsedStateKey, IsExpandedPredicate} from './expansionTogglingHelpers';
import {VisualCustomView} from './models/customViewHelpers';

/*
 * Types.
 */

export enum ParentTagOverridesEnum {
  /** The child tag is in its original nesting location. */
  NO_CHANGE = 'NO_CHANGE',
  /** The child has been moved from a nested location to root-level. */
  MOVED_TO_ROOT = 'MOVED_TO_ROOT',
}

/**
 * Defines nesting overrides via drag-and-drop reordering during edit mode.
 * These parent-child relationships aren't persisted until edit mode changes are saved.
 * For cases outside of edit mode, an empty object will suffice.
 */
export interface ParentTagOverridesMap {
  [childTagId: TagModel['id']]: TagModel['id'] | ParentTagOverridesEnum;
}

export interface ParentTagOverride {
  childTagId: TagModel['id'];
  newParentTagId: TagModel['id'] | ParentTagOverridesEnum;
}

interface CreateFlatVisualCustomViewParams {
  tag: TagModel;
  customView: CustomViewModel | undefined;
  type: CustomViewTypesEnum;
  isVisibleInWorkspace: boolean;
}

interface CreateVisualCustomViewParams {
  tag: TagModel;
  customView: CustomViewModel | undefined;
  nestingLevel: number;
  findIsExpanded: IsExpandedPredicate;
  defaultExpandedState: boolean;
  children: ReadonlyArray<VisualCustomView>;
  type: CustomViewTypesEnum;
  isVisibleInWorkspace: boolean;
}

export interface SelectableTag {
  isSelected: boolean;
  tag: TagModel;
}

interface BuildVisualCustomViewsPresortedParams {
  presortedSelectableTags: ReadonlyArray<SelectableTag>;
  customViews: ResourceMap<CustomViewModel>;
  findIsExpanded: IsExpandedPredicate;
  defaultExpandedState: boolean;
  parentTagOverridesMap: ParentTagOverridesMap | undefined;
  nestingLevelOffset?: number;
}

interface BuildVisualCustomViewPresortedParams {
  selectableTag: SelectableTag;
  selectableTagsByParent: Dictionary<ReadonlyArray<SelectableTag>>;
  customViews: ResourceMap<CustomViewModel>;
  findIsExpanded: IsExpandedPredicate;
  defaultExpandedState: boolean;
  nestingLevel: number;
  sortOrderMap: WorkspaceSortOrderMap;
}

/*
 * Constants.
 */

export const rootLevelParentId = 0;

export const defaultIsCustomViewExpanded = true;

/*
 * Helpers.
 */

/**
 * Builds a VisualCustomView list from a pre-sorted SelectableTag list.
 * The output represents a tag tree that may have several levels of hierarchy
 * and ensures any children are sorted properly according to the input.
 */
export function buildVisualCustomViewsPresorted({
  presortedSelectableTags,
  customViews,
  findIsExpanded,
  defaultExpandedState,
  parentTagOverridesMap = {},
  nestingLevelOffset = 0,
}: BuildVisualCustomViewsPresortedParams): ReadonlyArray<VisualCustomView> {
  // Group the tags by parent, prioritizing the overrides if provided.
  const selectableTagsByParent = groupBy(presortedSelectableTags, ({tag}) =>
    computeParentTagId(tag, parentTagOverridesMap),
  );

  const sortOrderMap = computeWorkspaceSortOrderMap(presortedSelectableTags, ({tag}) => tag.id);

  // Map recursively from the root level.
  const rootSelectableTags = selectableTagsByParent[rootLevelParentId] ?? [];
  return rootSelectableTags.map((rootSelectableTag) =>
    buildVisualCustomViewPresorted({
      selectableTag: rootSelectableTag,
      nestingLevel: nestingLevelOffset,
      selectableTagsByParent,
      customViews,
      findIsExpanded,
      defaultExpandedState,
      sortOrderMap,
    }),
  );
}

function buildVisualCustomViewPresorted({
  selectableTag,
  selectableTagsByParent,
  customViews,
  findIsExpanded,
  defaultExpandedState,
  nestingLevel,
  sortOrderMap,
}: BuildVisualCustomViewPresortedParams): VisualCustomView {
  const {tag, isSelected} = selectableTag;

  // Find the corresponding CustomViewModel instance in Redux state, if any.
  const customView = customViews[tag.id];

  const children = _(selectableTagsByParent[tag.id])
    .map((childSelectableTag) =>
      buildVisualCustomViewPresorted({
        selectableTag: childSelectableTag,
        selectableTagsByParent,
        customViews,
        findIsExpanded,
        defaultExpandedState,
        nestingLevel: nestingLevel + 1,
        sortOrderMap,
      }),
    )
    .sortBy(({id}) => sortOrderMap[id])
    .value();

  return createVisualCustomView({
    tag,
    customView,
    type: computeVisualCustomViewType(tag),
    isVisibleInWorkspace: isSelected,
    nestingLevel,
    findIsExpanded,
    defaultExpandedState,
    children,
  });
}

export function createVisualCustomView({
  tag,
  customView,
  type,
  isVisibleInWorkspace,
  nestingLevel,
  findIsExpanded,
  defaultExpandedState,
  children,
}: CreateVisualCustomViewParams): VisualCustomView {
  return {
    id: tag.id,
    uid: customView?.uid,
    namespace: tag.namespace,
    isExpanded: findIsExpanded(buildCustomViewExpandedKey(tag.id), defaultExpandedState),
    type,
    tag,
    nestingLevel,
    children,
    isVisibleInWorkspace,
  };
}

/** May be used to create a VisualCustomView that is solely intended to live in a flat hierarchy. */
export function createFlatVisualCustomView({
  tag,
  customView,
  type,
  isVisibleInWorkspace,
}: CreateFlatVisualCustomViewParams): VisualCustomView {
  return createVisualCustomView({
    tag,
    customView,
    type,
    isVisibleInWorkspace,
    nestingLevel: rootLevelParentId,
    findIsExpanded: dummyFindIsExpanded,
    defaultExpandedState: defaultIsCustomViewExpanded,
    children: [],
  });
}

export function computeVisualCustomViewTypeWithUserNamespace(
  tag: TagModel,
  userNamespace: string,
): CustomViewTypesEnum {
  return tag.namespace === userNamespace ? CustomViewTypesEnum.PRIVATE_TAG : CustomViewTypesEnum.TAG;
}

export function computeVisualCustomViewType(tag: TagModel): CustomViewTypesEnum {
  return isTeammateNamespace(tag.namespace) ? CustomViewTypesEnum.PRIVATE_TAG : CustomViewTypesEnum.TAG;
}

/** This can be used when the expanded value is irrelevant for the resulting VisualCustomView. */
export function dummyFindIsExpanded() {
  return true;
}

export function buildCustomViewExpandedKey(viewId: number): CollapsedStateKey {
  return `${CollapsedItemKeyPrefixesEnum.CUSTOM_VIEW}${viewId}`;
}

export function computeParentTagId(
  tag: TagModel,
  parentTagOverridesMap: ParentTagOverridesMap,
): TagModel['id'] | typeof rootLevelParentId {
  const parentTagIdOverride = parentTagOverridesMap[tag.id];

  if (parentTagIdOverride === ParentTagOverridesEnum.MOVED_TO_ROOT) {
    return rootLevelParentId;
  }

  const originalParentTagId = tag.parentTagId || rootLevelParentId;

  if (parentTagIdOverride === ParentTagOverridesEnum.NO_CHANGE) {
    return originalParentTagId;
  }

  return parentTagIdOverride || originalParentTagId;
}

export function computeParentTagOverridesMap(parentTagOverrides: ReadonlyArray<ParentTagOverride>) {
  return parentTagOverrides.reduce<ParentTagOverridesMap>(
    (result, parentTagOverride) => ({
      ...result,
      [parentTagOverride.childTagId]: parentTagOverride.newParentTagId,
    }),
    {},
  );
}

export function computeVisualCustomViewIsEqual(a: VisualCustomView, b: VisualCustomView): boolean {
  return isEqualWith(a, b, (value, otherValue, key, parent) => {
    // If we're comparing the visual custom view object itself, traverse its properties.
    if (!parent) {
      return undefined;
    }

    // If we're comparing the children array, traverse its members.
    if (parent === a && key === 'children') {
      return undefined;
    }

    // If we're comparing a child visual custom view, recurse.
    if (parent === a.children) {
      return computeVisualCustomViewIsEqual(value, otherValue);
    }

    // If we're comparing anything else, use identity equality.
    return value === otherValue;
  });
}
